import {
  BillItem,
  BillType,
  ClientInfoType,
  FullBillingInfo,
  IProductDiscountSetting,
  IProductTaxSetting,
  ProductType,
} from '../../../all-types'
import { ProductDiscountCalc } from './product-discount-calc'
import { ProductTaxCalc } from './product-tax-calc'

class BillCalculator {
  bill: BillType
  allproducts: ProductType[]
  clientInfo: ClientInfoType

  DEFAULT_FINAL_BILLING_INFO: FullBillingInfo = {
    totalItemsCostRaw: 0,
    totalDiscountIssued: 0,
    totalTaxCharged: 0,
    TaxChargeSheetView: {},
    finalBillAmount: 0,
  }

  constructor(
    bill: BillType,
    allproducts: ProductType[],
    clientInfo: ClientInfoType
  ) {
    this.bill = bill
    this.allproducts = allproducts
    this.clientInfo = clientInfo
  }

  getProcessedBillingInfo = (fullBill: BillType = this.bill) => {
    const tempBillingInfo = { ...this.DEFAULT_FINAL_BILLING_INFO }
    try {
      const { items } = fullBill
      tempBillingInfo.TaxChargeSheetView = {}
      tempBillingInfo.extraInfo = {}
      tempBillingInfo.extraInfo.roundOff = '0.00'
      items?.forEach((item) => {
        tempBillingInfo.totalItemsCostRaw += item.billingInfo.rawAmount
        tempBillingInfo.totalDiscountIssued +=
          item.billingInfo.totalDiscountGiven
        tempBillingInfo.totalTaxCharged += item.billingInfo.totalTaxCharged
        if (item.billingInfo.totalTaxCharged > 0) {
          item.billingInfo.tax.taxInfo?.forEach((tax) => {
            const taxName = tax.taxComponent.taxName
            if (tempBillingInfo.TaxChargeSheetView[taxName]) {
              tempBillingInfo.TaxChargeSheetView[taxName] += tax.taxAmount
            } else {
              tempBillingInfo.TaxChargeSheetView[taxName] = tax.taxAmount
            }

            // For displaying tax breakup in bill print
            tempBillingInfo.extraInfo![taxName] =
              `${tax.taxComponent.taxName} ${tax.taxComponent.applicableTax}%`
          })

          item?.billingInfo?.discounts?.discountsInfo?.forEach((discount) => {
            const discountName = discount.discountComponent.discountName
            tempBillingInfo.extraInfo!['discount'] =
              `${discountName ?? 'discount'} ${discount.discountComponent.applicableDiscount}%`
            tempBillingInfo.extraInfo!['discountPercentage'] =
              discount.discountComponent.applicableDiscount.toString()
            tempBillingInfo.extraInfo!['discountName'] = discountName
          })
        }
        tempBillingInfo.finalBillAmount = Number(
          (
            tempBillingInfo.finalBillAmount + item.billingInfo.finalAmount
          ).toFixed(2)
        )
      })

      const finalBillAmountWithDecimals = Number(
        tempBillingInfo.finalBillAmount.toFixed(2)
      )
      const roundedFinalBillAmount = Math.floor(finalBillAmountWithDecimals)

      tempBillingInfo.finalBillAmount = roundedFinalBillAmount
      tempBillingInfo.extraInfo.roundOff = (
        finalBillAmountWithDecimals - roundedFinalBillAmount
      ).toFixed(2)
    } catch (e) {
      console.error(e)
    }
    return tempBillingInfo
  }

  applyFullBillDiscount = (discountComp: string[]) => {
    try {
      this.bill.items?.forEach((item) => {
        const product = this.allproducts.find(
          (p) => p.productId === item.productId
        )
        console.log('Applying discount on item', item, product)
        if (product) {
          this.applyDiscountOnItem(item, product.discounts, discountComp)
          this.applyTaxOnItem(item, product.tax)
        }
      })
      const billingInfo = this.getProcessedBillingInfo(this.bill)
      this.bill.billingInfo = billingInfo
    } catch (e) {
      console.error(e)
    }
  }

  applyDiscountOnItem = (
    item: BillItem,
    productDiscountSettings: IProductDiscountSetting,
    fullBillDiscountCompos: string[] = []
  ) => {
    const productDiscountCalc = new ProductDiscountCalc(
      this.clientInfo.discountManager,
      productDiscountSettings
    )
    productDiscountCalc.applyDiscountByProductSettings(
      item.amount,
      fullBillDiscountCompos
    )
    item.billingInfo.discounts = productDiscountCalc.billDiscount
    item.billingInfo.totalDiscountGiven =
      productDiscountCalc.billDiscount.discountAmount
  }

  applyTaxOnItem = (item: BillItem, productTaxSettings: IProductTaxSetting) => {
    const productTaxCalc = new ProductTaxCalc(
      this.clientInfo.taxManager,
      productTaxSettings
    )
    const amountForTax = item.billingInfo.discounts.isDiscountApplied
      ? item.billingInfo.discounts.amountAfterDiscount
      : item.amount
    productTaxCalc.applyProductTax(amountForTax)
    const TOTAL_DISCOUNT = item.billingInfo.discounts.discountAmount
    const TOTAL_TAX = productTaxCalc.billTax.taxAmount
    item.billingInfo.tax = productTaxCalc.billTax
    item.billingInfo.totalTaxCharged = TOTAL_TAX
    item.billingInfo.finalAmount = item.amount - TOTAL_DISCOUNT + TOTAL_TAX
    item.billingInfo.rawAmount = item.amount
  }
}

export { BillCalculator }
